"use client";

import { createContext, useContext, useEffect, useRef, type CSSProperties, type ElementType, type ReactNode } from "react";
import { observeReveal, showReveal } from "@/lib/reveal";
import { cn } from "@/lib/utils";

type Direction = "up" | "in" | "scale" | "left";

// starting look of each direction (SSR + first paint) and how long it takes to settle
const hidden: Record<Direction, CSSProperties> = {
  up: { opacity: 0, transform: "translate3d(0, 24px, 0)" },
  in: { opacity: 0 },
  scale: { opacity: 0, transform: "scale(0.96)" },
  left: { opacity: 0, transform: "translate3d(-32px, 0, 0)" },
};
const duration: Record<Direction, number> = { up: 0.6, in: 0.7, scale: 0.6, left: 0.6 };

const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export function Reveal({
  children,
  className,
  delay = 0,
  direction = "up",
  as = "div",
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  direction?: Direction;
  as?: "div" | "span" | "p" | "li" | "section";
}) {
  const ref = useRef<HTMLElement>(null);
  const Comp = as as ElementType;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (reducedMotion()) {
      showReveal(el, { duration: 0, delay: 0 });
      return;
    }
    return observeReveal(el, { duration: duration[direction], delay }, 80);
  }, [direction, delay]);

  return (
    <Comp ref={ref} className={className} style={hidden[direction]}>
      {children}
    </Comp>
  );
}

const GroupContext = createContext({ stagger: 0.08, immediate: false });

export function RevealGroup({
  children,
  className,
  stagger = 0.08,
  as = "div",
  immediate = false,
}: {
  children: ReactNode;
  className?: string;
  stagger?: number;
  as?: "div" | "ul";
  /** Play on mount instead of when scrolled into view - for lists that re-render as filters change. */
  immediate?: boolean;
}) {
  const Comp = as as ElementType;
  return (
    <GroupContext.Provider value={{ stagger, immediate }}>
      <Comp data-reveal-group className={className}>
        {children}
      </Comp>
    </GroupContext.Provider>
  );
}

export function RevealItem({
  children,
  className,
  direction = "up",
}: {
  children: ReactNode;
  className?: string;
  direction?: Direction;
}) {
  const { stagger, immediate } = useContext(GroupContext);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (reducedMotion()) {
      showReveal(el, { duration: 0, delay: 0 });
      return;
    }
    // stagger by position inside the group (capped so late items in a long list don't wait seconds)
    const items = el.closest("[data-reveal-group]")?.querySelectorAll("[data-reveal-item]");
    const index = items ? Array.prototype.indexOf.call(items, el) : 0;
    const cfg = { duration: duration[direction], delay: Math.min(Math.max(index, 0) * stagger, 0.5) };
    if (immediate) {
      showReveal(el, cfg);
      return;
    }
    return observeReveal(el, cfg, 80);
  }, [direction, stagger, immediate]);

  return (
    <div ref={ref} data-reveal-item className={cn(className)} style={hidden[direction]}>
      {children}
    </div>
  );
}
